import React from "react";
import { Image, Popover, Tag } from "antd";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import {
  capitalizeFirstLetter,
  formatToLocalDate,
  getThumbnailUrl,
  ATTRIBUTE_ORDER,
  DecriptEnv,
  formatCurrency,
  getFullAwsUrl,
} from "../../../../helpers/utils";
import { EyeOutlined } from "@ant-design/icons";
import {
  ProductComponentTypeEnum,
  ProductComponentTypeLabels,
  ProductDropEnum,
  ProductDropLabels,
  ProductFinishEnum,
  ProductFinishLabels,
} from "../../Products/productsTypes";
import { useEffect, useState } from "react";
import { getAdditionalInfoFieldsAPI } from "../quotationApis";
import {
  AdditionalInfoField,
  AdditionalInfoValue,
  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT,
} from "../quotationTypes";
import { Spin, Tooltip } from "antd";
import { useAppDispatch } from "../../../../store";
import {
  SummaryFieldsSchema,
  SummaryBlockLabel,
  CategorySlugs,
} from "../Components/Products/SummaryHelper";
import { APRView } from "../Components/Products/Forms/APRForm";
import { svg } from "../../../../Assets/svg";
import ConfigureDropTBCVariantModal from "../../Orders/Components/Modals/ConfigureDropTBCVariantModal";
import { isCustomerLoggedIn } from "../../../../helpers/bycrypt";
import CommonFilePreviewModal from "../../../Components/CommonFilePreviewModal";
import DescriptionWithShowMore from "../../../../helpers/DescriptionWithShowMore";

// --- Configuration Constants ---

const IMG_URL_BASE = `${DecriptEnv(
  process.env.REACT_APP_AWS_ENDPOINT,
)}static-images/`;

const extractBaseFileName = (name: string) => {
  if (!name) return "";
  const parts = name.split("/");
  const lastPart = parts[parts.length - 1];
  if (lastPart.includes("_")) {
    return lastPart.substring(lastPart.indexOf("_") + 1);
  }
  return lastPart;
};

export const formatDrawingType = (type?: string) => {
  if (!type) return "";
  if (type === "factory_assembled") return "Factory Assembled";
  if (type === "same") return "Same";
  if (type === "custom") return "Custom";
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const AttachedDrawings: React.FC<{
  drawings: any[];
  setPreviewModal: any;
  setPreviewFiles: any;
  setPreviewIndex: any;
  layout?: "table" | "card" | "department";
}> = ({
  drawings,
  setPreviewModal,
  setPreviewFiles,
  setPreviewIndex,
  layout = "card",
}) => {
  if (!drawings || !Array.isArray(drawings) || drawings.length === 0) {
    return null;
  }

  const validDrawings = drawings.filter(
    (item) =>
      (Array.isArray(item?.drawing_image) && item.drawing_image.length > 0) ||
      item?.document ||
      item?.file_url ||
      typeof item === "string",
  );

  if (validDrawings.length === 0) return null;

  if (layout === "department") {
    const items: Array<{
      id: string;
      title: string;
      files: string[];
      fileIndex: number;
      type?: string;
      qty?: number;
    }> = [];

    validDrawings.forEach((item, idx) => {
      const isTyped =
        typeof item === "object" &&
        (item?.type === "factory_assembled" ||
          item?.type === "same" ||
          item?.type === "custom" ||
          item?.type === "manual");

      if (!isTyped && Array.isArray(item?.drawing_image) && item.drawing_image.length > 0) {
        item.drawing_image.forEach((file: string, fIdx: number) => {
          items.push({
            id: `variant-${idx}-${fIdx}`,
            title: extractBaseFileName(file),
            files: item.drawing_image,
            fileIndex: fIdx,
            qty: item.qty,
          });
        });
      } else {
        const fileDoc =
          typeof item === "string"
            ? item
            : item?.document ||
              item?.file_url ||
              (Array.isArray(item?.drawing_image) ? item.drawing_image[0] : null);
        if (fileDoc) {
          const title =
            typeof item === "object" && item?.title
              ? item.title
              : extractBaseFileName(fileDoc);
          items.push({
            id: `doc-${idx}`,
            title: title || "Drawing",
            files: [fileDoc],
            fileIndex: 0,
            type: typeof item === "object" ? item?.type : undefined,
          });
        }
      }
    });

    if (items.length === 0) return null;

    const renderPill = (item: (typeof items)[0]) => (
      <Tooltip
        key={item.id}
        title={
          <div style={{ fontSize: "12px" }}>
            <div className="fw-bold">{item.title}</div>
            {item.qty !== undefined && item.qty !== null && (
              <div>Qty: {item.qty}</div>
            )}
            {item.type && <div>Type: {formatDrawingType(item.type)}</div>}
            <div className="text-muted" style={{ fontSize: "10px" }}>
              Click to preview
            </div>
          </div>
        }
        placement="top"
      >
        <span
          className="badge d-inline-flex align-items-center gap-1 cursor-pointer"
          style={{
            backgroundColor: "#f5f5f5",
            color: "#262626",
            border: "1px solid #d9d9d9",
            borderRadius: "14px",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: 500,
            maxWidth: "140px",
            lineHeight: "1.2",
            userSelect: "none",
            transition: "all 0.2s ease",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setPreviewFiles(item.files);
            setPreviewIndex(item.fileIndex);
            setPreviewModal(true);
          }}
        >
          <EyeOutlined
            style={{ fontSize: "12px", flexShrink: 0, color: "#1890ff" }}
          />
          <span
            className="text-truncate"
            style={{
              maxWidth: "100px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
          </span>
          {item.qty !== undefined && item.qty !== null && (
            <span
              className="badge rounded-pill bg-secondary text-white px-1"
              style={{ fontSize: "9px", lineHeight: "1" }}
            >
              x{item.qty}
            </span>
          )}
        </span>
      </Tooltip>
    );

    if (items.length <= 3) {
      return (
        <div
          className="d-flex flex-wrap gap-1 align-items-center mt-1 w-100"
          style={{ maxWidth: "100%" }}
        >
          {items.map(renderPill)}
        </div>
      );
    }

    const popoverContent = (
      <div
        className="d-flex flex-column gap-1 p-1"
        style={{ maxWidth: "260px", maxHeight: "220px", overflowY: "auto" }}
      >
        <div
          className="fw-semibold text-secondary mb-1 border-bottom pb-1"
          style={{ fontSize: "12px" }}
        >
          Department Drawings ({items.length})
        </div>
        {items.map((item) => (
          <div
            key={item.id}
            className="d-flex align-items-center justify-content-between gap-2 p-1 rounded hover-bg cursor-pointer"
            style={{
              fontSize: "12px",
              border: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setPreviewFiles(item.files);
              setPreviewIndex(item.fileIndex);
              setPreviewModal(true);
            }}
          >
            <div
              className="d-flex align-items-center gap-1 text-truncate"
              style={{ flex: 1 }}
            >
              <EyeOutlined
                style={{ color: "#1890ff", fontSize: "12px", flexShrink: 0 }}
              />
              <span className="text-truncate" title={item.title}>
                {item.title}
              </span>
            </div>
            {item.type && (
              <span
                className="badge bg-light text-secondary border"
                style={{ fontSize: "10px" }}
              >
                {formatDrawingType(item.type)}
              </span>
            )}
          </div>
        ))}
      </div>
    );

    return (
      <div
        className="d-flex flex-wrap gap-1 align-items-center mt-1 w-100"
        style={{ maxWidth: "100%" }}
      >
        {items.slice(0, 3).map(renderPill)}
        <Popover content={popoverContent} trigger="hover" placement="right">
          <span
            className="badge rounded-pill cursor-pointer"
            style={{
              backgroundColor: "#e6f4ff",
              color: "#0958d9",
              border: "1px solid #91caff",
              fontSize: "11px",
              padding: "3px 8px",
              lineHeight: "1.2",
              userSelect: "none",
            }}
          >
            +{items.length - 3} more
          </span>
        </Popover>
      </div>
    );
  }

  if (layout === "table") {
    return (
      <div className="d-flex flex-column gap-1 w-100">
        {validDrawings.map((item, idx) => {
          const isTyped =
            typeof item === "object" &&
            (item?.type === "factory_assembled" ||
              item?.type === "same" ||
              item?.type === "custom" ||
              item?.type === "manual");

          if (
            !isTyped &&
            Array.isArray(item?.drawing_image) &&
            item.drawing_image.length > 0
          ) {
            return (
              <div
                key={idx}
                className="border p-1 w-100 rounded-2 d-flex gap-2 align-items-center"
                style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
              >
                {item?.qty !== undefined && item?.qty !== null && (
                  <span
                    className="fw-semibold text-secondary text-nowrap"
                    style={{ fontSize: "11px" }}
                  >
                    Qty: {item.qty}
                  </span>
                )}
                <div className="d-flex flex-wrap gap-1">
                  {item.drawing_image.map((file: string, index: number) => (
                    <Tag
                      key={index}
                      className="cursor-pointer text-truncate m-0"
                      style={{
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                        maxWidth: "100%",
                        height: "auto",
                        color: "#ffffff",
                        fontSize: "12px",
                        fontWeight: "bold",
                        backgroundColor: "#989898",
                      }}
                      title={extractBaseFileName(file)}
                      onClick={() => {
                        setPreviewFiles(item.drawing_image);
                        setPreviewIndex(index);
                        setPreviewModal(true);
                      }}
                    >
                      {extractBaseFileName(file)}
                    </Tag>
                  ))}
                </div>
              </div>
            );
          }

          const fileDoc =
            typeof item === "string"
              ? item
              : item?.document ||
                item?.file_url ||
                (Array.isArray(item?.drawing_image) ? item.drawing_image[0] : null);
          if (!fileDoc) return null;

          const displayLabel =
            typeof item === "object" && item?.title
              ? item.title
              : extractBaseFileName(fileDoc);

          return (
            <div
              key={idx}
              className="border p-1 w-100 rounded-2 d-flex gap-2 align-items-center justify-content-between"
              style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
            >
              <div className="d-flex flex-wrap gap-1 align-items-center">
                <Tag
                  className="cursor-pointer text-truncate m-0"
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "normal",
                    maxWidth: "100%",
                    height: "auto",
                    color: "#ffffff",
                    fontSize: "12px",
                    fontWeight: "bold",
                    backgroundColor: "#525252",
                  }}
                  title={displayLabel}
                  onClick={() => {
                    setPreviewFiles([fileDoc]);
                    setPreviewIndex(0);
                    setPreviewModal(true);
                  }}
                >
                  {displayLabel}
                </Tag>
              </div>
              {typeof item === "object" && item?.type && (
                <span
                  className="badge bg-secondary text-white"
                  style={{ fontSize: "10px" }}
                >
                  {formatDrawingType(item.type)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-2">
      {validDrawings.map((item, idx) => {
        const isTyped =
          typeof item === "object" &&
          (item?.type === "factory_assembled" ||
            item?.type === "same" ||
            item?.type === "custom" ||
            item?.type === "manual");

        if (
          !isTyped &&
          Array.isArray(item?.drawing_image) &&
          item.drawing_image.length > 0
        ) {
          return (
            <div
              key={idx}
              className="border px-2 py-2 rounded-2 row align-items-center m-0"
              style={{ backgroundColor: "#fafafa" }}
            >
              {item?.qty !== undefined && item?.qty !== null && (
                <div
                  className="col-12 col-sm-2 text-secondary fw-semibold"
                  style={{ fontSize: "12px" }}
                >
                  Qty: {item.qty}
                </div>
              )}
              <div
                className={
                  item?.qty !== undefined ? "col-12 col-sm-10" : "col-12"
                }
              >
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  {item.drawing_image.map((file: string, index: number) => {
                    const fname = extractBaseFileName(file);
                    return (
                      <Tooltip key={index} title={fname}>
                        <Tag
                          className="cursor-pointer text-truncate m-0 d-inline-flex align-items-center gap-1"
                          style={{
                            maxWidth: "200px",
                            backgroundColor: "#f5f5f5",
                            borderColor: "#d9d9d9",
                            color: "#262626",
                            padding: "2px 8px",
                            borderRadius: "6px",
                          }}
                          onClick={() => {
                            setPreviewFiles(item.drawing_image);
                            setPreviewIndex(index);
                            setPreviewModal(true);
                          }}
                        >
                          <EyeOutlined style={{ fontSize: "11px" }} />
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "160px" }}
                          >
                            {fname}
                          </span>
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }

        const fileDoc =
          typeof item === "string"
            ? item
            : item?.document ||
              item?.file_url ||
              (Array.isArray(item?.drawing_image) ? item.drawing_image[0] : null);
        if (!fileDoc) return null;

        const displayLabel =
          typeof item === "object" && item?.title
            ? item.title
            : extractBaseFileName(fileDoc);

        return (
          <div
            key={idx}
            className="border px-2 py-2 rounded-2 row align-items-center justify-content-between m-0"
            style={{ backgroundColor: "#fafafa" }}
          >
            <div className="col-12 col-sm-8 d-flex align-items-center gap-2">
              <Tooltip title={displayLabel}>
                <Tag
                  className="cursor-pointer text-truncate m-0 d-inline-flex align-items-center gap-1"
                  style={{
                    maxWidth: "100%",
                    fontSize: "12px",
                    fontWeight: 500,
                    backgroundColor: "#f0f5ff",
                    borderColor: "#adc6ff",
                    color: "#1d39c4",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                  onClick={() => {
                    setPreviewFiles([fileDoc]);
                    setPreviewIndex(0);
                    setPreviewModal(true);
                  }}
                >
                  <EyeOutlined style={{ fontSize: "11px" }} />
                  <span className="text-truncate" style={{ maxWidth: "260px" }}>
                    {displayLabel}
                  </span>
                </Tag>
              </Tooltip>
            </div>
            {typeof item === "object" && item?.type && (
              <div className="col-12 col-sm-4 text-sm-end mt-sm-0 mt-1">
                <span
                  className="badge bg-light text-secondary border"
                  style={{ fontSize: "11px" }}
                >
                  {formatDrawingType(item.type)}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ProductDetails = ({
  data,
  activeTab,
  categoryData,
  quoteId,
  isOrderView = false,
}: {
  data: any;
  activeTab?: string;
  categoryData?: any;
  quoteId?: string;
  isOrderView?: boolean;
}) => {
  const [additionalInfoFields, setAdditionalInfoFields] = useState<
    AdditionalInfoField[]
  >([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const dispatch = useAppDispatch();

  const showRetailPriceKey =
    data?.customer_snapshot?.show_retail_price !== false;
  const isRetailPriceVisible = !(isCustomerLoggedIn() && !showRetailPriceKey);
  const showHiddenRetailPriceTooltip =
    !isCustomerLoggedIn() && !showRetailPriceKey;

  const [resolvingVariantData, setResolvingVariantData] = useState<{
    parent: any;
    variant: any;
  } | null>(null);

  const areLeanProducts = categoryData?.is_lean_category === true;

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (
      activeTab === "additional-info" &&
      data?.categories_snapshot?.category_id
    ) {
      setLoadingFields(true);
      dispatch(
        getAdditionalInfoFieldsAPI({
          category: data.categories_snapshot.category_id,
          onSuccess: (res) => {
            const fields = res?.data || [];
            setAdditionalInfoFields(fields);
            setLoadingFields(false);
          },
          onError: () => setLoadingFields(false),
        }),
      );
    }
  }, [activeTab, data?.categories_snapshot?._id, dispatch]);

  if (!data) return null;

  const productsData = categoryData
    ? (data.products || []).find(
        (p: any) => p.category_id === categoryData.category_id,
      ) || {}
    : data.products || {};

  const productList = productsData.products || [];
  const accessoryList = productsData.accessories || [];
  const replacementPartList = productsData.replacement_parts || [];
  const additionalInfo = productsData.additional_info || {};
  const summaryList = productsData.summery || [];

  const totalProductsVariants = productList.reduce(
    (acc: number, item: any) => acc + (item.variants?.length || 0),
    0,
  );
  const totalAccessoriesVariants = accessoryList.reduce(
    (acc: number, item: any) => acc + (item.variants?.length || 0),
    0,
  );
  const totalReplacementPartsVariants = replacementPartList.reduce(
    (acc: number, item: any) => acc + (item.variants?.length || 0),
    0,
  );

  // --- Helpers ---
  const formatAttributes = (attrs: any) => {
    if (!attrs) return "";
    const parts = ATTRIBUTE_ORDER.filter((def) => attrs[def.key]).map((def) => {
      let val = attrs[def.key];
      const label = def.label.replace(/\s*\(.*\)/, "");
      let displayVal = val;

      if (def.key === "finish" || def.key === "secondary_finish") {
        displayVal = ProductFinishLabels[val as ProductFinishEnum] || val;
      } else if (def.key === "drop") {
        displayVal = ProductDropLabels[val as ProductDropEnum] || val;
      } else if (def.key === "custom_drop") {
        displayVal = `${val}mm`;
      } else if (typeof val === "string") {
        displayVal = val.split("_").map(capitalizeFirstLetter).join(" ");
      }

      return `${label}: ${displayVal}`;
    });

    const nonStandardColour =
      attrs.non_standard_product_colour ||
      attrs.non_standard_accessories_colour ||
      attrs.non_standard_replacement_parts_colour;
    const nonStandardPaintCode =
      attrs.non_standard_product_paint_code ||
      attrs.non_standard_accessories_paint_code ||
      attrs.non_standard_replacement_parts_paint_code;

    if (nonStandardColour) {
      parts.push(`Colour: ${nonStandardColour}`);
    }
    if (nonStandardPaintCode) {
      parts.push(`Paint Code: ${nonStandardPaintCode}`);
    }

    const nonStandardSecondaryColour =
      attrs.non_standard_secondary_product_colour ||
      attrs.non_standard_secondary_accessories_colour ||
      attrs.non_standard_secondary_replacement_parts_colour;
    const nonStandardSecondaryPaintCode =
      attrs.non_standard_secondary_product_paint_code ||
      attrs.non_standard_secondary_accessories_paint_code ||
      attrs.non_standard_secondary_replacement_parts_paint_code;

    if (nonStandardSecondaryColour) {
      parts.push(`Secondary Colour: ${nonStandardSecondaryColour}`);
    }
    if (nonStandardSecondaryPaintCode) {
      parts.push(`Secondary Paint Code: ${nonStandardSecondaryPaintCode}`);
    }

    return parts.join(" | ");
  };

  // --- Render Functions for Lists ---
  const renderProductList = (
    list: any[],
    type: "Product" | "Accessory" | "Replacement Part",
  ) => {
    if (!list || list.length === 0) {
      return (
        <div className="text-center text-muted py-3">
          No{" "}
          {type === "Product"
            ? "products"
            : type === "Accessory"
            ? "accessories"
            : "replacement parts"}{" "}
          added.
        </div>
      );
    }

    // Grouping items
    const grouped = list.reduce((acc: any, item: any) => {
      let gKey = "Other";
      if (areLeanProducts) {
        gKey = item.name || "Product";
      } else if (item.component_type) {
        gKey =
          item.component_type.slug ||
          (typeof item.component_type === "string"
            ? item.component_type
            : "Other");
      }

      if (!acc[gKey]) acc[gKey] = [];
      acc[gKey].push(item);
      return acc;
    }, {});

    // Maintain original order of appearance
    const sortedKeys = Object.keys(grouped);

    return (
      <>
        {sortedKeys.map((groupKey) => {
          const groupVariantCount = grouped[groupKey].reduce(
            (acc: number, item: any) => acc + (item.variants?.length || 0),
            0,
          );

          return (
            <div key={groupKey} className="mb-4">
              <div className="mb-3 mt-2">
                <h5
                  className="fs-6 fw-bold text-primary mb-0 pb-2 border-bottom d-flex align-items-center gap-2"
                  style={{ letterSpacing: "0.05em" }}
                >
                  {areLeanProducts && (
                    <div className="img-60 flex-shrink-0">
                      {(() => {
                        const firstItem = grouped[groupKey][0];
                        const validImages = [
                          firstItem?.pre_galvanised_image,
                          firstItem?.hot_dip_galvanised_image,
                          firstItem?.powder_coated_image,
                        ].filter(Boolean);

                        if (validImages.length === 0) {
                          return (
                            <Image
                              src={placeHolderImage}
                              alt={groupKey}
                              preview={false}
                              rootClassName="w-100 h-100"
                              className="rounded-2 object-fit-cover border w-100 h-100"
                            />
                          );
                        }

                        return (
                          <Image.PreviewGroup>
                            {validImages.map((imgKey, index) => (
                              <Image
                                key={imgKey}
                                src={getFullAwsUrl(imgKey)}
                                alt={groupKey}
                                fallback={placeHolderImage}
                                rootClassName={
                                  index === 0 ? "w-100 h-100" : "d-none"
                                }
                                className={
                                  index === 0
                                    ? "rounded-2 object-fit-cover border w-100 h-100"
                                    : ""
                                }
                                style={{
                                  display: index === 0 ? undefined : "none",
                                }}
                                preview={
                                  index === 0
                                    ? { mask: <EyeOutlined /> }
                                    : undefined
                                }
                              />
                            ))}
                          </Image.PreviewGroup>
                        );
                      })()}
                    </div>
                  )}
                  <div className="d-flex flex-column gap-1">
                    {areLeanProducts &&
                      grouped[groupKey][0]?.component_type?.name && (
                        <div className="mb-0">
                          <Tag>{grouped[groupKey][0].component_type.name}</Tag>
                        </div>
                      )}
                    <div className="d-flex align-items-center">
                      {areLeanProducts
                        ? groupKey
                        : groupKey === "Other"
                        ? `Other ${
                            type === "Replacement Part"
                              ? "Replacement Parts"
                              : type === "Accessory"
                              ? "Accessories"
                              : "Products"
                          }`
                        : grouped[groupKey][0]?.component_type?.name ||
                          ProductComponentTypeLabels[
                            groupKey as ProductComponentTypeEnum
                          ] ||
                          groupKey
                            .split("_")
                            .map(capitalizeFirstLetter)
                            .join(" ")}
                      <span className="badge custom-badge bg-dark text-white rounded-3 ms-2">
                        {groupVariantCount > 0 && `${groupVariantCount}`}
                      </span>
                    </div>
                  </div>
                </h5>
              </div>
              <div className="d-flex flex-column gap-2">
                {grouped[groupKey].map((parentItem: any) => {
                  const keyId =
                    parentItem.product_id ||
                    parentItem.accessory_id ||
                    parentItem.replaceparts_id ||
                    parentItem._id;

                  return parentItem.variants?.map(
                    (variant: any, vIdx: number) => {
                      const isDropTBCVariant =
                        variant?.variant_clone?.is_drop_tbc ?? false;
                      return (
                        <div
                          key={`${keyId}-${vIdx}`}
                          className="card border shadow-sm rounded-3 mb-0"
                        >
                          <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                            <div className="row gy-sm-3 gy-2">
                              {/* Image & Title */}
                              <div className="col-12">
                                <div className="d-flex gap-md-3 gap-2">
                                  <div className="img-60">
                                    {(() => {
                                      const validImages = [
                                        parentItem?.pre_galvanised_image,
                                        parentItem?.hot_dip_galvanised_image,
                                        parentItem?.powder_coated_image,
                                      ].filter(Boolean);

                                      if (validImages.length === 0) {
                                        return (
                                          <Image
                                            src={placeHolderImage}
                                            alt={parentItem.name}
                                            preview={false}
                                            rootClassName="w-100 h-100"
                                            className="rounded-2 object-fit-cover border w-100 h-100"
                                          />
                                        );
                                      }

                                      return (
                                        <Image.PreviewGroup>
                                          {validImages.map((imgKey, index) => (
                                            <Image
                                              key={imgKey}
                                              src={getFullAwsUrl(imgKey)}
                                              alt={parentItem.name}
                                              fallback={placeHolderImage}
                                              rootClassName={
                                                index === 0
                                                  ? "w-100 h-100"
                                                  : "d-none"
                                              }
                                              className={
                                                index === 0
                                                  ? "rounded-2 object-fit-cover border w-100 h-100"
                                                  : ""
                                              }
                                              style={{
                                                display:
                                                  index === 0
                                                    ? undefined
                                                    : "none",
                                              }}
                                              preview={
                                                index === 0
                                                  ? { mask: <EyeOutlined /> }
                                                  : undefined
                                              }
                                            />
                                          ))}
                                        </Image.PreviewGroup>
                                      );
                                    })()}
                                  </div>
                                  <div className="d-flex flex-column justify-content-center gap-1 w-100">
                                    <div className="d-flex align-items-start justify-content-between gap-2">
                                      <h6 className="fw-bold mb-0 text-dark">
                                        {variant?.variant_name ||
                                          parentItem?.name}
                                      </h6>
                                      <div className="d-flex align-items-center gap-2">
                                        {!isRetailPriceVisible && (
                                          <div className="text-center text-md-end ms-auto pe-lg-3">
                                            <small
                                              className="text-muted d-block fw-normal lh-1"
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              {parentItem?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold lh-1 fs-6">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                        {isOrderView && isDropTBCVariant && (
                                          <button
                                            type="button"
                                            className="btn btn-link p-0 svg-32"
                                            onClick={() => {
                                              setResolvingVariantData({
                                                parent: parentItem,
                                                variant: variant,
                                              });
                                            }}
                                          >
                                            {svg?.editBtn}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <small
                                      className="text-muted fw-normal text-wrap d-inline-block lh-normal"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {parentItem?.description && (
                                        <div className="text-wrap lh-tight mb-1">
                                          <DescriptionWithShowMore
                                            description={parentItem.description}
                                          />
                                        </div>
                                      )}
                                      {variant?.max_load_per_bay && (
                                        <div className="lh-tight mb-1">
                                          Max Load per Bay:{" "}
                                          {variant.max_load_per_bay}
                                        </div>
                                      )}
                                      {variant?.max_load_per_pair && (
                                        <div className="lh-tight">
                                          Max Load per Pair:{" "}
                                          {variant.max_load_per_pair}
                                        </div>
                                      )}
                                      {variant?.max_load_per_shelf && (
                                        <div className="lh-tight">
                                          Max Load per Shelf:{" "}
                                          {variant.max_load_per_shelf}
                                        </div>
                                      )}
                                    </small>
                                    {Number(
                                      variant?.is_bom ?? parentItem?.is_bom,
                                    ) === 2 &&
                                      Boolean(
                                        variant?.assembly_drawing_require ??
                                          parentItem?.assembly_drawing_require,
                                      ) && (
                                        <div className="my-1">
                                          <Tag
                                            color="default"
                                            className="rounded-pill px-3 py-0.5 fw-semibold"
                                            style={{ fontSize: "12px" }}
                                          >
                                            Factory Assembled -{" "}
                                            {variant?.factory_assembled ??
                                            parentItem?.factory_assembled
                                              ? "Yes"
                                              : "No"}
                                          </Tag>
                                        </div>
                                      )}
                                    {/* Financials Grid */}
                                    <div className="row gy-2 gx-4 gx-lg-5 justify-content-between text-start text-md-start">
                                      {isRetailPriceVisible && (
                                        <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                          <div className="d-flex align-items-start gap-1">
                                            <div>
                                              <small className="text-muted d-block fw-normal text-nowrap">
                                                Retail(RRP):
                                              </small>
                                              <span className="fw-bold">
                                                {formatCurrency(
                                                  variant.unit_price,
                                                  data?.currency,
                                                )}
                                              </span>
                                            </div>
                                            {showHiddenRetailPriceTooltip && (
                                              <Tooltip
                                                title={
                                                  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                }
                                              >
                                                <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                  {svg?.restrictedAvatarIcon}
                                                </span>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {isRetailPriceVisible && (
                                        <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                          <small className="text-muted d-block fw-normal text-nowrap">
                                            {parentItem?.labelInQuotation ||
                                              "Qty"}
                                            :
                                          </small>
                                          <span className="fw-bold">
                                            {variant.quantity}
                                          </span>
                                        </div>
                                      )}
                                      {isRetailPriceVisible && (
                                        <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                          <div className="d-flex align-items-start gap-1">
                                            <div>
                                              <small className="text-muted d-block fw-normal text-nowrap">
                                                Retail(RRP) Total:
                                              </small>
                                              <span className="fw-bold">
                                                {formatCurrency(
                                                  variant.total_price,
                                                  data?.currency,
                                                )}
                                              </span>
                                            </div>
                                            {showHiddenRetailPriceTooltip && (
                                              <Tooltip
                                                title={
                                                  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                }
                                              >
                                                <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                  {svg?.restrictedAvatarIcon}
                                                </span>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {isRetailPriceVisible && (
                                        <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                          <div className="d-flex align-items-start gap-1">
                                            <div>
                                              <small className="text-muted d-block fw-normal text-nowrap">
                                                Discount (
                                                {variant.discount_percentage ||
                                                  0}
                                                %):
                                              </small>
                                              <span className="fw-bold">
                                                {formatCurrency(
                                                  variant.discount_amount,
                                                  data?.currency,
                                                )}
                                              </span>
                                            </div>
                                            {showHiddenRetailPriceTooltip && (
                                              <Tooltip
                                                title={
                                                  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                }
                                              >
                                                <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                  {svg?.restrictedAvatarIcon}
                                                </span>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {isRetailPriceVisible && (
                                        <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                          <div className="d-flex align-items-start gap-1">
                                            <div>
                                              <small className="text-muted d-block fw-normal text-nowrap">
                                                Discounted Unit Price (
                                                {variant.discount_percentage ||
                                                  0}
                                                %):
                                              </small>
                                              <span className="fw-bold">
                                                {formatCurrency(
                                                  variant.discounted_unit_price,
                                                  data?.currency,
                                                )}
                                              </span>
                                            </div>
                                            {showHiddenRetailPriceTooltip && (
                                              <Tooltip
                                                title={
                                                  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                }
                                              >
                                                <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                  {svg?.restrictedAvatarIcon}
                                                </span>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {isRetailPriceVisible && (
                                        <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                          <div className="d-flex align-items-start gap-1">
                                            <div>
                                              <small className="text-muted d-block fw-normal text-nowrap">
                                                Total:
                                              </small>
                                              <span className="fw-bold">
                                                {formatCurrency(
                                                  variant.line_total,
                                                  data?.currency,
                                                )}
                                              </span>
                                            </div>
                                            {showHiddenRetailPriceTooltip && (
                                              <Tooltip
                                                title={
                                                  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                }
                                              >
                                                <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                  {svg?.restrictedAvatarIcon}
                                                </span>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {data?.installation_mark_up_required &&
                                      variant.installation_mark_up_amount &&
                                      isRetailPriceVisible ? (
                                        <div className="col-lg-auto col-md-auto col-12">
                                          <div className="d-flex align-items-start gap-1">
                                            <div>
                                              <small className="text-muted d-block fw-normal text-nowrap">
                                                Discounted Installation Cost(
                                                {variant.installation_mark_up_percentage ||
                                                  5}
                                                %):
                                              </small>
                                              <span className="fw-bold">
                                                {formatCurrency(
                                                  variant.installation_mark_up_amount,
                                                  data?.currency,
                                                  data?.conversion_rate,
                                                )}
                                              </span>
                                            </div>
                                            {showHiddenRetailPriceTooltip && (
                                              <Tooltip
                                                title={
                                                  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                }
                                              >
                                                <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                  {svg?.restrictedAvatarIcon}
                                                </span>
                                              </Tooltip>
                                            )}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>

                                {variant?.drawings?.length > 0 && (
                                  <AttachedDrawings
                                    drawings={variant?.drawings}
                                    setPreviewFiles={setPreviewFiles}
                                    setPreviewIndex={setPreviewIndex}
                                    setPreviewModal={setPreviewModalOpen}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    },
                  );
                })}
              </div>
            </div>
          );
        })}

        {isOrderView && (
          <ConfigureDropTBCVariantModal
            open={resolvingVariantData !== null}
            onClose={() => setResolvingVariantData(null)}
            editData={resolvingVariantData}
            quoteId={quoteId}
            orderId={data?._id}
          />
        )}

        {previewModalOpen && (
          <CommonFilePreviewModal
            visible={previewModalOpen}
            onClose={() => {
              setPreviewModalOpen(false);
              setPreviewFiles([]);
              setPreviewIndex(0);
            }}
            files={previewFiles}
            activeIndex={previewIndex}
            onActiveIndexChange={setPreviewIndex}
          />
        )}
      </>
    );
  };

  const renderSummary = () => {
    if (!summaryList || summaryList.length === 0) {
      return (
        <div className="text-center text-muted py-3">No summary added.</div>
      );
    }
    const slug = categoryData?.slug as CategorySlugs;
    if (!slug) return null;

    const schema = SummaryFieldsSchema[slug];
    const blockLabel = SummaryBlockLabel[slug] || "Block";

    if (!schema) return null;

    return summaryList.map((entry: any, index: number) => (
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        <div key={entry._id || index} className="mb-4">
          <h5 className="fs-6 fw-bold mb-3 pb-2 border-bottom text-primary">
            {blockLabel} {index + 1}
          </h5>
          {slug === CategorySlugs.ADJUSTABLE_PALLET_RACKING_APR ? (
            <APRView item={entry} />
          ) : (
            <div className="row gy-3">
              {schema.map((field) => {
                const key = field.key;
                let val = entry[key];

                // Handle objects like { label: 'Single Run', value: 'single-run' }
                if (val && typeof val === "object" && val.label) {
                  val = val.label;
                }

                if (val === undefined || val === null || val === "") {
                  return null;
                }

                return (
                  <div key={field.key} className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      {field.label}
                    </label>
                    <div className="fw-semibold text-dark fs-6 d-flex align-items-center gap-1">
                      {val}
                      {field.unit && <span> {field.unit}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    ));
  };

  return (
    <div className="d-flex flex-column gap-4">
      <h3 className="fs-4 fs-4-mobile fw-bold text-black d-flex flex-wrap column-gap-3 row-gap-1 align-items-center">
        <span className="order-sm-1 order-1">
          {categoryData?.product_type_name || "Storage"} Type:{" "}
        </span>
        <span className="text-primary fw-normal d-inline-flex align-items-center gap-2 order-sm-2 order-3">
          <img
            src={
              categoryData?.image
                ? getFullAwsUrl(categoryData?.image, true)
                : placeHolderImage
            }
            width={32}
            height={32}
            className="rounded-2 object-fit-cover"
            alt="Type"
          />{" "}
          {categoryData?.label || categoryData?.name || "-"}
        </span>
      </h3>
      <hr className="m-0" />

      {/* 0. Summary Section */}
      {activeTab === "summary" && (
        <div>
          {categoryData?.summary?.quoteData && (
            <div
              className="ck-read-editor"
              dangerouslySetInnerHTML={{
                __html: categoryData?.summary?.quoteData || "-",
              }}
            />
          )}

          <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">
            {categoryData?.slug
              ? SummaryBlockLabel[categoryData.slug as CategorySlugs]
              : "Summary"}
          </h4>

          {renderSummary()}
        </div>
      )}

      {/* 1. Products Section */}
      {activeTab === "products" && (
        <div>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-md-3 mb-2 gap-2">
            <h4 className="fs-5 fw-bold text-black mb-0 ">
              Products
              <span className="badge custom-badge bg-dark text-white rounded-3 ms-1">
                {totalProductsVariants > 0 && `${totalProductsVariants}`}
              </span>
            </h4>
            <span className="fs-5">
              Products Total Amount:{" "}
              <span className="fw-bold">
                {formatCurrency(
                  productsData.products_total_amount,
                  data?.currency,
                )}
              </span>
            </span>
          </div>
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            <div className="d-flex flex-column gap-2">
              {renderProductList(productList, "Product")}
            </div>
          </div>
        </div>
      )}

      {/* 2. Accessories Section */}
      {activeTab === "accessories" && (
        <div>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-md-3 mb-2">
            <h4 className="fs-5 fw-bold text-black mb-0">
              Accessories
              <span className="badge custom-badge bg-dark text-white rounded-3 ms-1">
                {totalAccessoriesVariants > 0 && `${totalAccessoriesVariants}`}
              </span>
            </h4>
            <span className="fs-5">
              Accessories Total Amount:{" "}
              <span className="fw-bold">
                {formatCurrency(
                  productsData.accessories_total_amount,
                  data?.currency,
                )}
              </span>
            </span>
          </div>
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
            <div className="d-flex flex-column gap-2">
              {renderProductList(accessoryList, "Accessory")}
            </div>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-center mb-md-3 mb-2">
            <h4 className="fs-5 fw-bold text-black mb-0">
              Replacement Parts
              <span className="badge custom-badge bg-dark text-white rounded-3 ms-1">
                {totalReplacementPartsVariants > 0 &&
                  `${totalReplacementPartsVariants}`}
              </span>
            </h4>
            <span className="fs-5">
              Replacement Parts Total Amount:{" "}
              <span className="fw-bold">
                {formatCurrency(
                  productsData.replacement_parts_total_amount,
                  data?.currency,
                )}
              </span>
            </span>
          </div>
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            <div className="d-flex flex-column gap-2">
              {renderProductList(replacementPartList, "Replacement Part")}
            </div>
          </div>
        </div>
      )}

      {/* 3. Additional Information Section */}
      {/* {activeTab === "additional-info" && (
        <div>
          <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">
            Additional Information
          </h4>
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            {loadingFields ? (
              <div className="text-center py-4">
                <Spin tip="Loading information..." />
              </div>
            ) : (
              <div className="row gy-md-4 gy-3">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    {data?.categories_snapshot?.product_type_name ||
                      "Pallets Rack"}{" "}
                    Type
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    {data.categories_snapshot?.image && (
                      <img
                        src={getFullAwsUrl(
                          data.categories_snapshot.image,
                          true,
                        )}
                        width={30}
                        height={30}
                        className="object-fit-cover rounded-1 border"
                        alt="Category"
                      />
                    )}
                    <span className="fw-semibold text-dark fs-6">
                      {data.categories_snapshot?.name || "-"}
                    </span>
                  </div>
                </div>

                {additionalInfoFields.map((field) => {
                  const val = additionalInfo[field.key];
                  const hasValue =
                    val !== undefined &&
                    val !== null &&
                    (Array.isArray(val) ? val.length > 0 : true);

                  const renderDynamicValue = (slug: any) => {
                    let option: AdditionalInfoValue | undefined;
                    if (field.type === "toggle_with_boolean") {
                      option = field.value.find(
                        (v) => v.slug === (slug === true ? "yes" : "no"),
                      );
                    } else {
                      option = field.value.find((v) => v.slug === slug);
                    }

                    if (!option)
                      return (
                        <span className="fw-semibold text-dark fs-6">
                          {String(slug)}
                        </span>
                      );

                    const showDate =
                      option.slug === "installation_required" ||
                      option.slug === "installation-work"
                        ? additionalInfo.installation_date
                        : null;

                    return (
                      <div className="d-flex align-items-center gap-2 mb-1">
                        {option.image && (
                          <img
                            src={getFullAwsUrl(option.image, true)}
                            width={30}
                            height={30}
                            className="object-fit-cover rounded-1 border"
                            alt={option.name}
                          />
                        )}
                        <div>
                          <span className="fw-semibold text-dark fs-6 lh-m">
                            {option.name}
                          </span>
                          {showDate && (
                            <span
                              className="d-block fs-8 text-949494"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Date: {formatToLocalDate(showDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div key={field._id} className="col-lg-3 col-md-4 col-sm-6">
                      <label className="fs-6 fw-normal text-secondary d-block mb-1">
                        {field.name}
                      </label>
                      <div className="d-flex flex-column">
                        {!hasValue ? (
                          <span className="fw-semibold text-dark fs-6">-</span>
                        ) : Array.isArray(val) ? (
                          val.map((v) => (
                            <React.Fragment key={v}>
                              {renderDynamicValue(v)}
                            </React.Fragment>
                          ))
                        ) : (
                          renderDynamicValue(val)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default ProductDetails;
